import type { Folder, Note, Todo } from '../types'

/** How often a backup may be taken, when something has actually changed. */
export const BACKUP_INTERVAL = 30 * 60_000

/** Backups kept per user. The oldest is pruned once a new one lands. */
export const MAX_BACKUPS = 20

/** Everything needed to rebuild an account from nothing. */
export interface Snapshot {
  version: 1
  takenAt: number
  notes: Note[]
  folders: Folder[]
  todos: Todo[]
}

export interface BackupFile {
  /** Storage object name, which sorts chronologically. */
  name: string
  takenAt: number
}

export function buildSnapshot(
  notes: Note[], folders: Folder[], todos: Todo[], takenAt: number,
): Snapshot {
  // Trashed notes are included on purpose: they are still recoverable in the
  // app, so a backup that dropped them would lose data the user has not.
  return { version: 1, takenAt, notes, folders, todos }
}

/**
 * A cheap signature of everything worth backing up.
 *
 * Only the fields a backup would restore, so a backup is skipped when nothing
 * has actually changed — the alternative is 48 identical copies a day, which
 * would push the real history out of a 20-slot window within a day.
 */
export function fingerprint(notes: Note[], folders: Folder[], todos: Todo[]): string {
  const n = notes.map((x) => `${x.id}:${x.updatedAt}:${x.trashedAt ?? 0}:${x.folderId ?? ''}:${x.pinned ? 1 : 0}`)
  const f = folders.map((x) => `${x.id}:${x.name}:${x.order}`)
  const t = todos.map((x) => `${x.id}:${x.updatedAt}:${x.status}:${x.order}`)
  return `${n.sort().join('|')}#${f.sort().join('|')}#${t.sort().join('|')}`
}

/** Whether a backup is due: something changed, and the interval has elapsed. */
export function backupDue(
  lastAt: number | null, lastPrint: string | null, print: string, now: number,
  interval = BACKUP_INTERVAL,
): boolean {
  if (!print) return false            // nothing loaded yet
  if (lastPrint === print) return false
  if (lastAt === null) return true    // first of the session, and it differs
  return now - lastAt >= interval
}

/** Object name for a backup, padded so lexical order is chronological. */
export function backupName(takenAt: number): string {
  return `${String(takenAt).padStart(16, '0')}.json`
}

export function takenAtFrom(name: string): number {
  return Number(name.replace(/\.json$/, '').replace(/^0+/, '')) || 0
}

/** The backups to delete so that at most `max` remain, oldest first. */
export function prunableBackups(files: BackupFile[], max = MAX_BACKUPS): string[] {
  if (files.length <= max) return []
  return [...files]
    .sort((a, b) => b.takenAt - a.takenAt)
    .slice(max)
    .map((f) => f.name)
}
